/**
 * Which conductors feed each circuit of a board: the phase of a single-phase circuit on a
 * three-phase board (its `phase` property, or L1, L2, L3 in turn in board order), all three for
 * a three-phase circuit. The multi-line generator draws these, the phase balance check
 * (R-CALC-06) adds up the loads on them, so both always agree.
 */
import type { Element, Id, Project } from '@elec/domain';
import type { Analysis, CircuitInfo } from './topology.ts';
import { boardCircuits } from './sync.ts';

export const PHASES = ['L1', 'L2', 'L3'] as const;
export type Phase = (typeof PHASES)[number];

const num = (v: unknown) =>
  typeof v === 'number' ? v : typeof v === 'string' && v !== '' ? Number(v) : undefined;

/** Poles of the circuit's device as drawn on the multi-line diagram: 2 (L + N), 3 or 4. */
export function circuitPoles(c: CircuitInfo): number {
  const p = num(c.device.props.poles);
  if (p && p >= 3) return c.cable.cores === 4 ? 3 : 4;
  if (/-[34]p$/.test(c.device.symbolId)) return c.device.symbolId.endsWith('3p') ? 3 : 4;
  return c.cable.cores === 5 ? 4 : 2;
}

/** Is the board three-phase (3N×400 V supply or a 4-pole main device)? `devices`: on its sheet. */
export function isThreePhaseBoard(project: Project, devices: Element[]): boolean {
  if (/400/.test(project.meta.supplyVoltage)) return true;
  const main = devices.find((e) => e.symbolId === 'iec.switch-disconnector' || e.symbolId === 'iec.rcd');
  return (num(main?.props.poles) ?? 2) >= 4;
}

/**
 * A board's circuits in multi-line column order: those directly under the origin RCD first, then
 * one group per further RCD (in the order the groups are met).
 */
export function columnOrder(circuits: CircuitInfo[]): CircuitInfo[] {
  const origin = circuits.map((c) => c.rcds.at(-1)).find(Boolean);
  const groups = new Map<Id, CircuitInfo[]>();
  const direct: CircuitInfo[] = [];
  for (const c of circuits) {
    const nearest = c.rcds[0];
    if (!nearest || nearest.id === origin?.id) direct.push(c);
    else groups.set(nearest.id, [...(groups.get(nearest.id) ?? []), c]);
  }
  return [...direct, ...[...groups.values()].flat()];
}

export interface BoardPhases {
  three: boolean;
  /** Conductors of each circuit by device id: ['L2'] (single-phase), ['L1','L2','L3'], or ['L']. */
  of: Map<Id, string[]>;
}

/** The phases each circuit of the board drawn on `fromSheetId` is fed from. */
export function boardPhases(project: Project, analysis: Analysis, fromSheetId: Id): BoardPhases {
  const devices = Object.values(project.elements).filter((e) => e.sheetId === fromSheetId);
  const three = isThreePhaseBoard(project, devices);
  const of = new Map<Id, string[]>();
  let rr = 0;
  for (const c of columnOrder(boardCircuits(analysis, fromSheetId))) {
    if (!three) of.set(c.device.id, ['L']);
    else if (circuitPoles(c) > 2) of.set(c.device.id, [...PHASES]);
    else {
      const ph = String(c.device.props.phase ?? '');
      of.set(c.device.id, [(PHASES as readonly string[]).includes(ph) ? ph : PHASES[rr++ % 3]!]);
    }
  }
  return { three, of };
}
